import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { WeighbridgeInput } from "../../components/WeighbridgeInput/WeighbridgeInput";
import { NewPartnerForm } from "../../forms/NewPartnerForm/NewPartnerForm";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { TransactionType } from "../../models/Transaction";
import { MercantileType, ParamSettings } from "../../models/ParamSettings";
import { RoleType } from "../../models/Role";
import { getMercantileTemplate } from "../../document-templates/MercantileTemplate";
import { Weighbridge } from "../../weight-bridge/Weighbridge";
import { getWeighbridge } from "../../weight-bridge/Weighbridge";
import { useApplicationContext } from "../../application/ApplicationContext";
import { activeUser } from "../../services/AuthService";
import { srpsCalculation } from "../../services/SprsService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import { getAllSettings } from "../../services/SettingsService";
import {
  getOneDocument,
  addNewDocument,
  updateOneDocument,
} from "../../services/NewVersionService";
import "./NewDocumentOfReceivePage.scss";

export interface DocumentOfReceivePageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const NewDocumentOfReceivePage = (p: DocumentOfReceivePageProps) => {
  const { show, buttonSaveStyle, buttonNewPartnerStyle, buttonResetStyle } = p;

  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { state, serialPortSet, serialPortValueSet } = useApplicationContext();

  const buildEmptyMercantileInput = (id?: string): DocumentOfReceive => ({
    id: id ?? "",
    warehouse:
      activeUser?.role?.name === RoleType.Warehouseman
        ? (cloneDeep(activeUser.warehouse) as Warehouse)
        : ({} as Warehouse),
    warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
    type: TransactionType.Import,
    date: new Date(),
    time: new Date(),
    brutto: undefined,
    tara: undefined,
    netto: undefined,
    srps: undefined,
    product: {} as Product,
    productWithCode: "",
    partner: {} as Partner,
    shipper: {} as Partner,
    driverName: "",
    vehicleNumber: "",
    moisture: undefined,
    impurity: undefined,
    breakage: undefined,
    defect: undefined,
    hectoliter: undefined,
    protein: undefined,
    gluten: undefined,
    cellNumberFloor: undefined,
    hybrid: "",
    sentQuantity: 0,
    dryingCosts: undefined,
    dryWeight: undefined,
    shippingSlip: "",
    calculationMethod: true,
    foreignProduct: false,
    productOwner: {} as Partner,
    note: "",
    dispositionId: "",
    finishedDocument: false,
    finishedDocumentFirstTime: false,
    mercantile: true,
    input: true,
  });

  const [updating, setUpdating] = useState<boolean>(false);
  const [newDocumentOfReceive, setNewDocumentOfReceive] =
    useState<DocumentOfReceive>(() => buildEmptyMercantileInput(id));

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    [],
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [settings, setSettings] = useState<ParamSettings[]>([]);

  const [productCategory, setProductCategory] = useState<ProductCategory>();
  const [disabledByInputDate, setDisabledByInputDate] =
    useState<boolean>(false);
  const [okButtonDisabled, setOkButtonDisabled] = useState<boolean>(false);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [processing, setProcessing] = useState<boolean>(false);

  const [pdfData, setPdfData] = useState<any>(null);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const unwrapArray = (resp: any): any[] => {
      const d = resp?.data;
      if (d?.result && Array.isArray(d.result)) return d.result;
      if (Array.isArray(d)) return d;
      return [];
    };

    (async () => {
      try {
        setProcessing(true);

        const [whResp, partnersResp, catResp, prodResp, settingsResp] =
          await Promise.all([
            getAllWarehouses(),
            getAllPartners(),
            getAllProductCategories(),
            getAllProducts(),
            getAllSettings(),
          ]);

        setWarehouses(unwrapArray(whResp) as Warehouse[]);
        setPartners(unwrapArray(partnersResp) as Partner[]);
        setProductCategories(unwrapArray(catResp) as ProductCategory[]);
        setProducts(unwrapArray(prodResp) as Product[]);
        setSettings(unwrapArray(settingsResp) as ParamSettings[]);
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju šifarnika"),
          life: 3000,
        });
      } finally {
        setProcessing(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (isNew) {
      setUpdating(false);
      setDisabledByInputDate(false);
      setIsPdfReady(true);
      setProcessing(false);

      setNewDocumentOfReceive(buildEmptyMercantileInput(id));
      setProductCategory(undefined);
      return;
    }

    (async () => {
      try {
        setProcessing(true);
        setIsPdfReady(false);

        const resp = await getOneDocument(id);
        const document: any = resp?.data?.result ?? resp?.data;

        if (document) {
          if (document.date) {
            document.date = new Date(document.date);
            if (document.date.getTime() > new Date().getTime())
              setDisabledByInputDate(true);
            if (document.status === null) document.date = new Date();
          }
          if (document.time) {
            document.time = new Date(document.time);
            if (document.status === null) document.time = new Date();
          }
          if (!document.warehouseman) {
            document.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
          }

          setNewDocumentOfReceive(document);
          setProductCategory(document.product?.category);
          setUpdating(true);
          setIsPdfReady(true);
        }
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju dokumenta"),
          life: 3000,
        });
      } finally {
        setProcessing(false);
      }
    })();
  }, [id, isNew]);

  const getCategoryType = (categoryName?: string): MercantileType => {
    if (!categoryName) return MercantileType.Wheat;
    switch (categoryName) {
      case "Uljana repica":
        return MercantileType.OiledBeet;
      case "Ječam":
        return MercantileType.Barley;
      case "Soja":
        return MercantileType.Soya;
      case "Suncokret":
        return MercantileType.Sunflower;
      case "Kukuruz":
        return MercantileType.Corn;
      default:
        return MercantileType.Wheat;
    }
  };

  // populate pdf when data changes
  useEffect(() => {
    const generatePdf = () => {
      if (!isPdfReady || !settings.length) return;
      const categoryType = getCategoryType(
        newDocumentOfReceive.product?.category?.name,
      );
      const newPdf = getMercantileTemplate(
        categoryType,
        true,
        newDocumentOfReceive,
        settings,
      );
      setPdfData(newPdf);
    };

    const timer = setTimeout(generatePdf, 100);
    return () => clearTimeout(timer);
  }, [newDocumentOfReceive, isPdfReady, settings]);

  const toast = useRef(null);

  //* -------------- WEIGHBRIDGE START ----------------

  const [weighbridge, setWeighbridge] = useState<Weighbridge>();
  const [readableStreamClosed, setReadableStreamClosed] = useState<any>();
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);
  const readableStreamClosedRef = useRef<Promise<void> | null>(
    Promise.resolve(),
  );

  const disconnectSerial = async () => {
    try {
      await readerRef.current?.cancel();
      await readerRef.current?.releaseLock();
      readerRef.current = null;

      await readableStreamClosedRef.current?.catch(() => {});
      readableStreamClosedRef.current = null;

      if (state.serialPort) await state.serialPort.close();
    } catch (e) {
      console.error(e);
    } finally {
      serialPortSet(undefined);
    }
  };

  const connectSerial = async (_weighbridge: Weighbridge) => {
    try {
      let _port;

      if (state.serialPort && state.serialPort.readable) {
        _port = state.serialPort;
      } else {
        _port = await window.navigator.serial.requestPort();
        serialPortSet(_port);
        await _port.open({ baudRate: _weighbridge.baudRate });
      }

      const decoder = new TextDecoderStream();
      const inputDone = _port.readable.pipeTo(decoder.writable);
      const lineReadableStream = new TransformStream(
        _weighbridge.getLineBreakTransformer(),
      );
      const newReadableStreamClosed =
        decoder.readable.pipeThrough(lineReadableStream);

      const lineReader = lineReadableStream.readable.getReader();

      setReadableStreamClosed(newReadableStreamClosed);

      readerRef.current = lineReader;
      readableStreamClosedRef.current = inputDone;

      let previousValue = "";
      while (true) {
        if (!_port || !_port.readable) continue;

        const { value, done } = await lineReader.read();
        if (done) {
          break;
        }

        if (previousValue !== value) {
          serialPortValueSet(value);

          fetch("https://jvck.io/save/rwa-serial-port", {
            method: "POST",
            headers: { "Content-Type": "text/plain" },
            body: JSON.stringify({
              serialPortValue: value,
            }),
            mode: "no-cors",
          });
        }

        previousValue = value;
      }
    } catch (e) {
      console.error(e);
    } finally {
      await disconnectSerial();
    }
  };

  useEffect(() => {
    if (!newDocumentOfReceive.warehouse?.weighbridgeModel) return;
    const _weighbridge = getWeighbridge(
      newDocumentOfReceive.warehouse.weighbridgeModel,
    );

    if (!_weighbridge) return;
    setWeighbridge(_weighbridge);

    connectSerial(_weighbridge);
  }, [newDocumentOfReceive.warehouse]);

  useEffect(() => {
    fetch("https://jvck.io/save/rwa-serial-port", {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({
        warehouse: newDocumentOfReceive.warehouse,
        serialPortValue: state.serialPortValue,
        weight: newDocumentOfReceive.brutto,
        tara: newDocumentOfReceive.tara,
      }),
      mode: "no-cors",
    });
  }, [
    newDocumentOfReceive.brutto,
    newDocumentOfReceive.tara,
    newDocumentOfReceive.warehouse,
  ]);

  //* --------------- WEIGHBRIDGE END -----------------

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    setOkButtonDisabled(true);
    let correctChanges: boolean = true;
    let error: string = "";
    switch (newDocumentOfReceive.product?.category?.name) {
      case "Kukuruz": {
        const cornSettings = settings.find(
          (product) => product.mercantile === MercantileType.Corn,
        );
        if (
          (cornSettings?.impurityMax ?? 0) <
          (newDocumentOfReceive?.impurity ?? 0)
        ) {
          error = t("Primese kukuruza su veće od dozvoljenih");
          correctChanges = false;
        }
        if (
          (cornSettings?.defectMax ?? 0) < (newDocumentOfReceive?.defect ?? 0)
        ) {
          error = t("Defekt kukuruza je veći od dozvoljenog");
          correctChanges = false;
        }
        if (
          (cornSettings?.breakageMax ?? 0) <
          (newDocumentOfReceive?.breakage ?? 0)
        ) {
          error = t("Lom kukuruza je veći od dozvoljenog");
          correctChanges = false;
        }
        if (
          (cornSettings?.moistureMax ?? 0) <
          (newDocumentOfReceive?.moisture ?? 0)
        ) {
          error = t("Vlaga kukuruza je veća od dozvoljene");
          correctChanges = false;
        }
        break;
      }
      case "Soja": {
        const soyaSettings = settings.find(
          (product) => product.mercantile === MercantileType.Soya,
        );
        if (
          (soyaSettings?.moistureMax ?? 0) <
          (newDocumentOfReceive?.moisture ?? 0)
        ) {
          error = t("Vlaga soje je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (soyaSettings?.impurityMax ?? 0) <
          (newDocumentOfReceive?.impurity ?? 0)
        ) {
          error = t("Primese soje su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Suncokret": {
        const sunflowerSettings = settings.find(
          (product) => product.mercantile === MercantileType.Sunflower,
        );
        if (
          (sunflowerSettings?.moistureMax ?? 0) <
          (newDocumentOfReceive?.moisture ?? 0)
        ) {
          error = t("Vlaga suncokreta je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (sunflowerSettings?.impurityMax ?? 0) <
          (newDocumentOfReceive?.impurity ?? 0)
        ) {
          error = t("Primese suncokreta su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Uljana repica": {
        const oiledBeetSettings = settings.find(
          (product) => product.mercantile === MercantileType.OiledBeet,
        );
        if (
          (oiledBeetSettings?.moistureMax ?? 0) <
          (newDocumentOfReceive?.moisture ?? 0)
        ) {
          error = t("Vlaga uljane repice je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (oiledBeetSettings?.impurityMax ?? 0) <
          (newDocumentOfReceive?.impurity ?? 0)
        ) {
          error = t("Primese uljane repice su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Ječam": {
        const barleySettings = settings.find(
          (product) => product.mercantile === MercantileType.Barley,
        );
        if (
          (barleySettings?.hectoliterMin ?? 0) >
          (newDocumentOfReceive?.hectoliter ?? 0)
        ) {
          error = t("Hektolitar ječma je manji od dozvoljenog");
          correctChanges = false;
        }
        if (
          (barleySettings?.moistureMax ?? 0) <
          (newDocumentOfReceive?.moisture ?? 0)
        ) {
          error = t("Vlaga ječma je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (barleySettings?.impurityMax ?? 0) <
          (newDocumentOfReceive?.impurity ?? 0)
        ) {
          error = t("Primese ječma su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
      case "Pšenica": {
        const wheatSettings = settings.find(
          (product) => product.mercantile === MercantileType.Wheat,
        );
        if (
          (wheatSettings?.hectoliterMin ?? 0) >
          (newDocumentOfReceive?.hectoliter ?? 0)
        ) {
          error = t("Hektolitar pšenice je manji od dozvoljenog");
          correctChanges = false;
        }
        if (
          (wheatSettings?.moistureMax ?? 0) <
          (newDocumentOfReceive?.moisture ?? 0)
        ) {
          error = t("Vlaga pšenice je veća od dozvoljene");
          correctChanges = false;
        }
        if (
          (wheatSettings?.impurityMax ?? 0) <
          (newDocumentOfReceive?.impurity ?? 0)
        ) {
          error = t("Primese pšenice su veće od dozvoljenih");
          correctChanges = false;
        }
        break;
      }
    }

    if (correctChanges) {
      //calculation netto, srps, dryingCosts, dryWeight
      let categoryType: MercantileType = MercantileType.Wheat;
      if (newDocumentOfReceive.product?.category?.name === "Uljana repica")
        categoryType = MercantileType.OiledBeet;
      if (newDocumentOfReceive.product?.category?.name === "Ječam")
        categoryType = MercantileType.Barley;
      if (newDocumentOfReceive.product?.category?.name === "Soja")
        categoryType = MercantileType.Soya;
      if (newDocumentOfReceive.product?.category?.name === "Suncokret")
        categoryType = MercantileType.Sunflower;
      if (newDocumentOfReceive.product?.category?.name === "Kukuruz")
        categoryType = MercantileType.Corn;

      const [srps, , , , , , , , , , , dryingCosts, dispersal] =
        srpsCalculation(
          categoryType,
          (newDocumentOfReceive.brutto ?? 0) - (newDocumentOfReceive.tara ?? 0),
          newDocumentOfReceive.moisture ?? 0,
          newDocumentOfReceive.impurity ?? 0,
          newDocumentOfReceive.breakage ?? 0,
          newDocumentOfReceive.defect ?? 0,
          newDocumentOfReceive.hectoliter ?? 0,
          newDocumentOfReceive.calculationMethod as boolean,
          settings,
        );

      let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
      copyOfNewDocumentOfReceive.netto =
        newDocumentOfReceive.brutto !== undefined &&
        newDocumentOfReceive.tara !== undefined
          ? newDocumentOfReceive?.brutto - newDocumentOfReceive?.tara
          : 0;
      copyOfNewDocumentOfReceive.srps = Math.round(srps);
      copyOfNewDocumentOfReceive.dryingCosts = Math.round(dryingCosts);
      copyOfNewDocumentOfReceive.dryWeight =
        categoryType === MercantileType.Corn
          ? Math.round(srps - dryingCosts)
          : Math.round(srps);

      const expectedSrps = srpsCalculation(
        categoryType,
        (newDocumentOfReceive.brutto ?? 0) - (newDocumentOfReceive.tara ?? 0),
        newDocumentOfReceive.moisture ?? 0,
        newDocumentOfReceive.impurity ?? 0,
        newDocumentOfReceive.breakage ?? 0,
        newDocumentOfReceive.defect ?? 0,
        newDocumentOfReceive.hectoliter ?? 0,
        newDocumentOfReceive.calculationMethod as boolean,
        settings,
      )[0];
      if (copyOfNewDocumentOfReceive.srps !== expectedSrps) {
        copyOfNewDocumentOfReceive.srps = expectedSrps;
      }

      if (newDocumentOfReceive?.finishedDocumentFirstTime !== true) {
        copyOfNewDocumentOfReceive.date = new Date(); //ako nije zakljucena prijemnica prvi put,
        copyOfNewDocumentOfReceive.time = new Date(); //datum i vreme se formiraju kao trenutak zakljucenja prijemnice prvi put
      }

      copyOfNewDocumentOfReceive.status = ReceiveStatus.Finished; //zakljucena prijemnica, promenjen status u finished
      copyOfNewDocumentOfReceive.finishedDocumentFirstTime = true; //zakljucena prijemnica prvi put

      if (!copyOfNewDocumentOfReceive.warehouseman) {
        copyOfNewDocumentOfReceive.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
      }
      if (copyOfNewDocumentOfReceive.sentQuantity?.toString() == "")
        copyOfNewDocumentOfReceive.sentQuantity = 0;

      if (updating) {
        updateOneDocument(id as string, copyOfNewDocumentOfReceive).then(
          (response: AxiosResponse) => {
            if (response.status === 200 || response.status === 201) {
              //@ts-ignore
              toast.current.show({
                severity: "success",
                detail: t(`Uspešno popunjena prijemnica`),
                life: 3000,
              });
              if (newDocumentOfReceive?.isNatural) {
                navigate(`/processing-product-inputs`);
              } else {
                navigate(`/mercantile-input-fast`);
              }
              setOkButtonDisabled(false);
            } else if (response.status === 503) {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Sistemska greška: pokušaj snimanja prijemnice bez svog broja, pokušajte ponovo`,
                ),
                life: 3000,
              });
              setOkButtonDisabled(false);
            } else {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Dokument nije snimljen, proverite podatke o prijemu robe`,
                ),
                life: 3000,
              });
              setOkButtonDisabled(false);
            }
          },
        );
      } else {
        const payload: any = { ...copyOfNewDocumentOfReceive };
        if (!payload.id || payload.id === "") {
          delete payload.id;
        }
        addNewDocument(payload).then((response: AxiosResponse) => {
          if (response.status === 200 || response.status === 201) {
            //@ts-ignore
            toast.current.show({
              severity: "success",
              detail: t(`Uspešno izmenjena prijemnica`),
              life: 3000,
            });
            if (newDocumentOfReceive?.isNatural) {
              navigate(`/processing-product-inputs`);
            } else {
              navigate(`/mercantile-input-fast`);
            }
            setOkButtonDisabled(false);
          } else {
            //@ts-ignore
            toast.current.show({
              severity: "error",
              detail: t(
                `Dokument nije izmenjen, proverite podatke o prijemu robe`,
              ),
              life: 3000,
            });
            setOkButtonDisabled(false);
          }
        });
      }
    } else {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: error,
        life: 3000,
      });
      setOkButtonDisabled(false);
    }
  };

  const onError = () => {
    setOkButtonDisabled(true);
    //@ts-ignore
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
    setOkButtonDisabled(false);
  };

  const saveDraftVersion = () => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    if (!copyOfNewDocumentOfReceive.warehouseman) {
      copyOfNewDocumentOfReceive.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
    }
    copyOfNewDocumentOfReceive.status = ReceiveStatus.Arrived;
    if (copyOfNewDocumentOfReceive.sentQuantity?.toString() == "")
      copyOfNewDocumentOfReceive.sentQuantity = 0;

    //obavezno je izabrati proizvod
    if (newDocumentOfReceive.product && newDocumentOfReceive.product?.id) {
      //snimanje delimicno unetih podataka, bez validacije
      if (updating) {
        updateOneDocument(id as string, copyOfNewDocumentOfReceive).then(
          (response: AxiosResponse) => {
            console.log(response);
            //@ts-ignore
            toast.current.show({
              severity: "success",
              detail: t(`Uspešno popunjena prijemnica`),
              life: 3000,
            });
            if (newDocumentOfReceive?.isNatural) {
              navigate(`/processing-product-inputs`);
            } else {
              navigate(`/mercantile-input-fast`);
            }
          },
        );
      } else {
        const payload: any = { ...copyOfNewDocumentOfReceive };
        if (!payload.id || payload.id === "") {
          delete payload.id;
        }
        addNewDocument(payload).then(
          (response: AxiosResponse) => {
            if (response.status === 200 || response.status === 201) {
              //@ts-ignore
              toast.current.show({
                severity: "success",
                detail: t(`Uspešno izmenjena prijemnica`),
                life: 3000,
              });
              if (newDocumentOfReceive?.isNatural) {
                navigate(`/processing-product-inputs`);
              } else {
                navigate(`/mercantile-input-fast`);
              }
            } else {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Dokument nije izmenjen, proverite podatke o prijemu robe`,
                ),
                life: 3000,
              });
            }
          },
        );
      }
      if (newDocumentOfReceive?.isNatural) {
        navigate(`/processing-product-inputs`);
      } else {
        navigate(`/mercantile-input-fast`);
      }
    } else {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t(`Nije izabran naziv robe`),
        life: 3000,
      });
    }
  };

  const handleResetForm = () => {
    setNewDocumentOfReceive({
      id: id ?? "",
      warehouse:
        activeUser?.role?.name === RoleType.Warehouseman
          ? (cloneDeep(activeUser.warehouse) as Warehouse)
          : ({} as Warehouse),
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      type: TransactionType.Import,
      date: new Date(),
      time: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      srps: undefined,
      product: {} as Product,
      productWithCode: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "",
      vehicleNumber: "",
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      sentQuantity: 0,
      calculationMethod: true,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "",
      dispositionId: "",
      finishedDocument: false,
      mercantile: true,
      input: true,
    });
  };

  const handleNewDocumentOfReceive = (prop: string, value: any) => {
    setNewDocumentOfReceive((prev) => {
      const updatedDoc = {
        ...prev,
        [prop]: value,
      };

      // Automatski izračunaj neto ako se menja brutto ili tara
      if (prop === "brutto" || prop === "tara") {
        updatedDoc.netto = (updatedDoc.brutto ?? 0) - (updatedDoc.tara ?? 0);
      }

      return updatedDoc;
    });

    // Forsiraj ažuriranje PDF-a nakon svake promene
    setPdfData(null);
    setTimeout(() => {
      const categoryType = getCategoryType(
        newDocumentOfReceive.product?.category?.name,
      );
      const newPdf = getMercantileTemplate(
        categoryType,
        true,
        { ...newDocumentOfReceive, [prop]: value },
        settings,
      );
      setPdfData(newPdf);
    }, 150);
  };

  const handleNewRegistrationNumber = (prop: string, value: any) => {
    setNewDocumentOfReceive((newDocumentOfReceive) => ({
      ...newDocumentOfReceive,
      [prop]: value.toUpperCase(),
    }));
  };

  const handlePartnerChange = (prop: string, value: any) => {
    setNewDocumentOfReceive((prev) => {
      const partner = partners.find((item) => item.id == value);
      const updatedDoc = {
        ...prev,
        partner: partner ? { ...partner } : ({} as Partner),
        productOwner: partner ? { ...partner } : ({} as Partner),
      };

      const categoryType = getCategoryType(updatedDoc.product?.category?.name);
      const newPdf = getMercantileTemplate(
        categoryType,
        true,
        updatedDoc,
        settings,
      );
      setPdfData(newPdf);

      return updatedDoc;
    });
  };

  const handleShipperChange = (prop: string, value: any) => {
    setNewDocumentOfReceive((prev) => {
      const shipper = partners.find((item) => item.id == value);
      const updatedDoc = {
        ...prev,
        shipper: shipper ? { ...shipper } : ({} as Partner),
      };

      const categoryType = getCategoryType(updatedDoc.product?.category?.name);
      const newPdf = getMercantileTemplate(
        categoryType,
        true,
        updatedDoc,
        settings,
      );
      setPdfData(newPdf);

      return updatedDoc;
    });
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewDocumentOfReceive = cloneDeep(newDocumentOfReceive);
    activeUser?.role?.name === RoleType.Warehouseman
      ? (copyOfNewDocumentOfReceive.warehouse = cloneDeep(
          activeUser.warehouse,
        ) as Warehouse)
      : (copyOfNewDocumentOfReceive.warehouse = warehouses.find(
          (item) => item.id == value,
        ) as Warehouse);

    setNewDocumentOfReceive(copyOfNewDocumentOfReceive);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategories.find(
      (item) => item.id == value,
    ) as ProductCategory;
    setProductCategory(copyOfNewProductCategory);
  };

  const handleProductChange = (prop: string, value: any) => {
    setNewDocumentOfReceive((prev) => {
      const product = products.find((item) => item.id == value);
      const updatedDoc = {
        ...prev,
        product: product ? { ...product } : ({} as Product),
      };

      const categoryType = getCategoryType(updatedDoc.product?.category?.name);
      const newPdf = getMercantileTemplate(
        categoryType,
        true,
        updatedDoc,
        settings,
      );
      setPdfData(newPdf);

      return updatedDoc;
    });
  };

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
    addButtonSaveStyle.margin = "0 .5rem";
  }

  let addButtonNewPartnerStyle: React.CSSProperties = {};
  if (buttonNewPartnerStyle) {
    addButtonNewPartnerStyle = buttonNewPartnerStyle;
  } else {
    addButtonNewPartnerStyle.background = "rgba(21, 115, 71, 1)";
    addButtonNewPartnerStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Prijemnica broj")}: `}
              {newDocumentOfReceive?.finishedDocumentNumber ??
                newDocumentOfReceive.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Sve prijemnice")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                if (newDocumentOfReceive?.isNatural) {
                  navigate(`/processing-product-inputs`);
                } else {
                  navigate(`/mercantile-input-fast`);
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti prijemnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati prijemnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              {activeUser?.role?.name === RoleType.Warehouseman ? (
                <TextInput
                  id="warehouse"
                  value={activeUser?.warehouse?.name ?? ""}
                  onValueChange={handleWarehouseChange}
                  labelText={`${t("Magacin")} *`}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  disabled={
                    activeUser?.role?.name === RoleType.Warehouseman ||
                    disabledByInputDate === true
                  }
                />
              ) : (
                <DropdownInput
                  id="warehouse"
                  value={newDocumentOfReceive.warehouse.id}
                  onValueChange={handleWarehouseChange}
                  optionValue="id"
                  optionLabel="name"
                  options={warehouses.filter((item) => item.virtual !== true)}
                  filter={false}
                  labelText={`${t("Magacin")} *`}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{ required: true }}
                  disabled={
                    //@ts-ignore
                    activeUser?.role?.name === RoleType.Warehouseman ||
                    disabledByInputDate === true
                  }
                />
              )}
              <DateInput
                id="date"
                value={
                  newDocumentOfReceive?.finishedDocumentFirstTime
                    ? (newDocumentOfReceive.date as Date)
                    : new Date()
                }
                onValueChange={handleNewDocumentOfReceive}
                labelText={`${t("Datum")} *`}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                maxDate={new Date()}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman ||
                  disabledByInputDate === true
                }
              />
              <TextInput
                id="warehouseman"
                value={newDocumentOfReceive.warehouseman as string}
                onValueChange={handleNewDocumentOfReceive}
                labelText={`${t("Magacioner")} *`}
                placeholderText={t("Unesite ime magacionera")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: false }}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <DateInput
                id="time"
                value={
                  newDocumentOfReceive?.finishedDocumentFirstTime
                    ? (newDocumentOfReceive.time as Date)
                    : new Date()
                }
                onValueChange={handleNewDocumentOfReceive}
                labelText={`${t("Vreme")} *`}
                placeholderText={t("Izaberite vreme")}
                timeOnly={true}
                icon="bi bi-clock"
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  activeUser?.role?.name === RoleType.Warehouseman ||
                  disabledByInputDate === true
                }
              />
              <DropdownInput
                id="partner"
                value={newDocumentOfReceive.partner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handlePartnerChange}
                options={partners}
                filter={true}
                labelText={`${t("Dobavljač")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <div className="new-partner-button">
                <NewPartnerForm
                  buttonLabel={`${t("Dodaj dobavljača")}`}
                  buttonIcon="bi bi-plus-square"
                  show={show}
                  buttonStyle={{
                    border: "none",
                    background: "rgba(21, 115, 71, 1)",
                    color: "#ffffff",
                    marginRight: "1rem",
                    margin: "0 1rem 0 0",
                  }}
                  buttonNewCityStyle={{
                    border: "none",
                    background: "rgba(21, 115, 71, 1)",
                  }}
                  buttonSaveStyle={{
                    border: "none",
                    background: "rgba(0, 165, 83, 1)",
                  }}
                  buttonInactiveStyle={{
                    border: "none",
                    background: "rgba(108, 117, 125, 1)",
                  }}
                />
              </div>
              <DropdownInput
                id="shipper"
                value={newDocumentOfReceive.shipper?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleShipperChange}
                options={partners}
                filter={true}
                labelText={t("Prevoznik")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                //rules={{ required: true }}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <TextInput
                id="driverName"
                value={newDocumentOfReceive.driverName as string}
                onValueChange={handleNewDocumentOfReceive}
                labelText={`${t("Ime vozača")} *`}
                placeholderText={t("Unesite ime vozača")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <TextInput
                id="vehicleNumber"
                value={(
                  newDocumentOfReceive.vehicleNumber as string
                )?.toUpperCase()}
                onValueChange={handleNewRegistrationNumber}
                labelText={`${t("Registarski broj vozila")} *`}
                placeholderText={t("Unesite registarski broj")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                }}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <TextInputWithIcon
                id="shippingSlip"
                value={newDocumentOfReceive.shippingSlip as string}
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Otpremni listić")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <TextInputWithIcon
                id="sentQuantity"
                value={newDocumentOfReceive.sentQuantity as number}
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Upućena količina")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a number",
                  },
                }}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <TextInputWithIcon
                id="cellNumberFloor"
                value={newDocumentOfReceive.cellNumberFloor as string}
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Broj ćelije / podni magacin")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <DropdownInput
                id="productCategory"
                value={productCategory?.id}
                onValueChange={handleProductCategoryChange}
                optionValue="id"
                optionLabel="name"
                options={productCategories.filter(
                  (item) => item.type == ProductType.MercantileGoods,
                )}
                filter={false}
                labelText={`${t("Vrsta robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <DropdownInput
                id="productName"
                value={newDocumentOfReceive.product?.id}
                onValueChange={handleProductChange}
                optionValue="id"
                optionLabel="name"
                options={products.filter(
                  (item) => item.category?.id === productCategory?.id,
                )}
                filter={false}
                labelText={`${t("Naziv robe")} *`}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={
                  productCategory === undefined ||
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <WeighbridgeInput
                id="brutto"
                value={(newDocumentOfReceive.brutto as number) ?? ""}
                onValueChange={handleNewDocumentOfReceive}
                labelText={`${t("Bruto")} *`}
                icon="bi bi-minecart-loaded"
                iconAlign="right"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a number",
                  },
                }}
                weighbridge={weighbridge}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  !newDocumentOfReceive.warehouse ||
                  disabledByInputDate === true
                }
              />
              <WeighbridgeInput
                id="tara"
                value={(newDocumentOfReceive.tara as number) ?? ""}
                onValueChange={handleNewDocumentOfReceive}
                labelText={`${t("Tara")} *`}
                icon="bi bi-minecart"
                iconAlign="right"
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d+$/,
                    message: "Please enter a number",
                  },
                }}
                weighbridge={weighbridge}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  !newDocumentOfReceive.warehouse ||
                  disabledByInputDate === true
                }
              />
              <TextInputWithIcon
                id="moisture"
                value={(newDocumentOfReceive.moisture as number) ?? ""}
                onValueChange={handleNewDocumentOfReceive}
                labelText={
                  newDocumentOfReceive.calculationMethod === true
                    ? `${t("Vlaga")} *`
                    : t("Vlaga")
                }
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={`${t("Unesite procenat vlage kao ceo broj, sa jednom ili dve decimale (npr 11.90)")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={
                  newDocumentOfReceive.calculationMethod === true
                    ? {
                        required: true,
                        min: 0,
                        max: 100,
                        pattern: {
                          value: /^\d+(\.\d{1,2})?$/,
                          message: "Please enter a number",
                        },
                      }
                    : { required: false }
                }
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              <TextInputWithIcon
                id="impurity"
                value={newDocumentOfReceive.impurity as number}
                onValueChange={handleNewDocumentOfReceive}
                labelText={
                  newDocumentOfReceive.calculationMethod === true
                    ? `${t("Primese")} *`
                    : t("Primese")
                }
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText={`${t("Unesite procenat primesa kao ceo broj, sa jednom ili dve decimale")}`}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={
                  newDocumentOfReceive.calculationMethod === true
                    ? {
                        required: true,
                        min: 0,
                        max: 100,
                        pattern: {
                          value: /^\d+(\.\d{1,2})?$/,
                          message: "Please enter a number",
                        },
                      }
                    : { required: false }
                }
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  disabledByInputDate === true
                }
              />
              {newDocumentOfReceive?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="breakage"
                  value={(newDocumentOfReceive.breakage as number) ?? ""}
                  onValueChange={handleNewDocumentOfReceive}
                  labelText={
                    newDocumentOfReceive.calculationMethod === true
                      ? t("Lom *")
                      : t("Lom")
                  }
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite procenat loma kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newDocumentOfReceive.calculationMethod === true
                      ? {
                          required: true,
                          min: 0,
                          max: 100,
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: "Please enter a number",
                          },
                        }
                      : { required: false }
                  }
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              )}
              {newDocumentOfReceive?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="defect"
                  value={(newDocumentOfReceive.defect as number) ?? ""}
                  onValueChange={handleNewDocumentOfReceive}
                  labelText={
                    newDocumentOfReceive.calculationMethod === true
                      ? t("Defekt *")
                      : t("Defekt")
                  }
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite procenat defekta kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newDocumentOfReceive.calculationMethod === true
                      ? {
                          required: true,
                          min: 0,
                          max: 100,
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: "Please enter a number",
                          },
                        }
                      : { required: false }
                  }
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              )}
              {(newDocumentOfReceive?.product?.category?.name === "Pšenica" ||
                newDocumentOfReceive?.product?.category?.name === "Ječam") && (
                <TextInputWithIcon
                  id="hectoliter"
                  value={(newDocumentOfReceive.hectoliter as number) ?? ""}
                  onValueChange={handleNewDocumentOfReceive}
                  labelText={
                    newDocumentOfReceive.calculationMethod === true
                      ? t("Hektolitar *")
                      : t("Hektolitar")
                  }
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite hektolitar kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={
                    newDocumentOfReceive.calculationMethod === true
                      ? {
                          required: true,
                          min: 0,
                          max: 100,
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: "Please enter a number",
                          },
                        }
                      : { required: false }
                  }
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              )}
              {newDocumentOfReceive?.product?.category?.name === "Pšenica" && (
                <TextInputWithIcon
                  id="protein"
                  value={(newDocumentOfReceive.protein as number) ?? ""}
                  onValueChange={handleNewDocumentOfReceive}
                  labelText={t("Protein")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText={`${t("Unesite protein kao ceo broj, sa jednom ili dve decimale")}`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    pattern: {
                      value: /^\d+(\.\d{1,2})?$/,
                      message: "Please enter a number",
                    },
                  }}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              )}
              {newDocumentOfReceive?.product?.category?.name === "Ječam" && (
                <div className="empty-field"></div>
              )}
              {newDocumentOfReceive?.isNatural !== true && (
                <RadioButtonInput
                  id="foreignProduct"
                  value={newDocumentOfReceive.foreignProduct}
                  radioButtonValue1={t("DA")}
                  radioButtonValue2={t("NE")}
                  onValueChange={handleNewDocumentOfReceive}
                  labelText={t("Tuđa roba na skladištenju")}
                  control={control}
                  errors={errors}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              )}
              {newDocumentOfReceive?.isNatural !== true && (
                <RadioButtonInput
                  id="calculationMethod"
                  value={newDocumentOfReceive.calculationMethod}
                  radioButtonValue1={t("Svodjenje na SRPS")}
                  radioButtonValue2={t("Neto")}
                  onValueChange={handleNewDocumentOfReceive}
                  labelText={t("Metod obračuna")}
                  control={control}
                  errors={errors}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              )}
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newDocumentOfReceive.note as string}
                  onValueChange={handleNewDocumentOfReceive}
                  rows={1}
                  cols={50}
                  labelText={t("Napomena")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              </div>
              <RadioButtonInput
                id="finishedDocument"
                value={newDocumentOfReceive.finishedDocument}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleNewDocumentOfReceive}
                labelText={t("Zaključena prijemnica")}
                control={control}
                errors={errors}
                disabled={
                  (activeUser?.role?.name === RoleType.Warehouseman &&
                    newDocumentOfReceive?.status === ReceiveStatus.Finished) ||
                  activeUser?.role?.name === RoleType.Logistician ||
                  disabledByInputDate === true
                }
              />
              <div className="footer-buttons">
                <ButtonInput
                  labelText={t("Sačuvaj")}
                  onButtonClick={
                    newDocumentOfReceive.finishedDocument === true
                      ? handleSubmit(onSubmit, onError)
                      : saveDraftVersion
                  }
                  addStyle={addButtonSaveStyle}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true ||
                    okButtonDisabled
                  }
                />
                <ButtonInput
                  labelText={t("Resetuj")}
                  onButtonClick={handleResetForm}
                  addStyle={addButtonResetStyle}
                  disabled={
                    (activeUser?.role?.name === RoleType.Warehouseman &&
                      newDocumentOfReceive?.status ===
                        ReceiveStatus.Finished) ||
                    disabledByInputDate === true
                  }
                />
              </div>
            </div>
            <div className="documents-preview">
              {pdfData ? (
                <PdfPreview
                  pdfDefinition={pdfData}
                  downloadDocument={downloadPdfDocument}
                  printDocument={printPdfDocument}
                  documentName={`Prijemnica_${newDocumentOfReceive.finishedDocumentId ?? ""}`}
                />
              ) : (
                <p>Učitavam PDF prikaz...</p>
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
